import { useEffect, useRef } from 'react';
import { Routes, Route, Navigate } from 'react-router-dom';
import { useAuth } from 'react-oidc-context';

import Layout from './routes/Layout';
import Dashboard from './routes/Dashboard';
import Search from './routes/Search';
import Sectors from './routes/Sectors';
import Reports from './routes/Reports';
import SyncHistory from './routes/SyncHistory';
import HouseholdDetail from './routes/HouseholdDetail';
import PersonDetail from './routes/PersonDetail';
import InteractionsList from './routes/InteractionsList';
import Changes from './routes/Changes';
import TagsAdmin from './routes/TagsAdmin';
import Incidents from './routes/Incidents';
import IncidentBoard from './routes/IncidentBoard';
import IncidentPacket from './routes/IncidentPacket';
import AuthCallback from './routes/AuthCallback';
import { currentPath, isSigningOut, type SigninState } from './auth';

export default function App() {
  const auth = useAuth();
  // One sign-in attempt per page load: a failed silent renew sets auth.error,
  // which re-runs this effect while the redirect is already under way.
  const attempted = useRef(false);

  // Signing out in one tab clears the shared user from localStorage; follow
  // suit here, or this tab's next silent renew would write a fresh one back.
  useEffect(() => {
    const onStorage = (e: StorageEvent) => {
      if (e.key?.startsWith('oidc.user:') && e.newValue === null) void auth.removeUser();
    };
    window.addEventListener('storage', onStorage);
    return () => window.removeEventListener('storage', onStorage);
  }, [auth]);

  useEffect(() => {
    if (
      !attempted.current &&
      !isSigningOut() &&
      !auth.isLoading &&
      !auth.isAuthenticated &&
      !auth.activeNavigator &&
      window.location.pathname !== '/auth/callback'
    ) {
      attempted.current = true;
      const redirect = () =>
        auth.signinRedirect({ state: { returnTo: currentPath() } satisfies SigninState });
      // A tab opened after the hour-long ID token lapsed still holds the
      // refresh token; trade it in quietly before falling back to the login page.
      if (auth.user?.refresh_token) auth.signinSilent().catch(redirect);
      else void redirect();
    }
  }, [auth]);

  if (auth.isLoading) return <Centered>Signing you in…</Centered>;
  if (auth.error) return <Centered>Auth error: {auth.error.message}</Centered>;

  if (!auth.isAuthenticated) {
    return (
      <Routes>
        <Route path="/auth/callback" element={<AuthCallback />} />
        <Route path="*" element={<Centered>Redirecting to sign in…</Centered>} />
      </Routes>
    );
  }

  return (
    <Routes>
      <Route path="/auth/callback" element={<AuthCallback />} />
      <Route path="/incidents/:id/packet" element={<IncidentPacket />} />
      <Route element={<Layout />}>
        <Route path="/" element={<Dashboard />} />
        <Route path="/search" element={<Search />} />
        <Route path="/reports" element={<Reports />} />
        <Route path="/maps" element={<Sectors />} />
        {/* The page was /sectors until it grew into the map builder; keep
            anyone's bookmark working. */}
        <Route path="/sectors" element={<Navigate to="/maps" replace />} />
        <Route path="/syncs" element={<SyncHistory />} />
        <Route path="/people/:id" element={<PersonDetail />} />
        <Route path="/households/:id" element={<HouseholdDetail />} />
        <Route path="/interactions" element={<InteractionsList />} />
        <Route path="/tags" element={<TagsAdmin />} />
        <Route path="/changes" element={<Changes />} />
        <Route path="/incidents" element={<Incidents />} />
        <Route path="/incidents/:id" element={<IncidentBoard />} />
        <Route path="*" element={<Navigate to="/" replace />} />
      </Route>
    </Routes>
  );
}

function Centered({ children }: { children: React.ReactNode }) {
  return <div className="flex h-full items-center justify-center text-slate-500 dark:text-slate-400">{children}</div>;
}
